import { Link } from "react-router-dom";
import { OrderStatusBadge } from "@/components/orders/order-status";
import { useLeadOrders } from "@/hooks/useOrders";
import { formatMxn } from "@/lib/format-mxn";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/**
 * The lead's purchase history, under its products of interest.
 *
 * Self-fetching, like `LeadVisitSection`: orders are not in the lead-detail
 * payload. The caller renders it only for a client whose plan has `orders`.
 *
 * `abandoned` carts are left out. One is written every time a chat session
 * rotates over an open cart, so on a regular customer they would outnumber the
 * real purchases and bury them; they say nothing staff can act on. An `open`
 * cart stays — it is what the customer is buying right now.
 *
 * Each card lists what the order carried — quantity, product and line total —
 * so staff can tell one purchase from another without opening it. An «Envío»
 * row follows when a delivery fee was charged (`null` renders none, `0` renders
 * it at zero, as on the order detail): without it the lines would not add up to
 * the total above them. The struck-out list price stays on the detail page.
 *
 * Hides itself when there is nothing to show, so a lead who never bought gets no
 * empty box.
 */
export function LeadOrdersSection({ leadId }: { leadId: string }): React.JSX.Element | null {
  const { data } = useLeadOrders(leadId, true);

  const orders = (data?.items ?? []).filter((o) => o.status !== "abandoned");
  if (orders.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-ink-300">
        Historial de pedidos
      </p>
      <div className="flex flex-col gap-2">
        {orders.map((o) => (
          <Link
            key={o.id}
            to={`/pedidos/${o.id}`}
            className="flex flex-col gap-1.5 rounded-[12px] border border-ink-100 bg-white p-2.5 transition-colors hover:bg-ink-050"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-ink-900">{formatDate(o.createdAt)}</span>
              <span className="text-xs font-semibold text-ink-900">
                {formatMxn(o.totalCents, o.currency)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <OrderStatusBadge status={o.status} />
              <span className="text-[11px] text-ink-500">
                {o.itemCount} {o.itemCount === 1 ? "artículo" : "artículos"}
              </span>
            </div>
            {o.lines.length > 0 && (
              <ul className="flex flex-col gap-0.5 border-t border-ink-100 pt-1.5">
                {o.lines.map((l) => (
                  <li
                    key={l.productId}
                    className="flex items-baseline justify-between gap-2 text-[11px] text-ink-600"
                  >
                    <span className="min-w-0 break-words">
                      {l.quantity}× {l.productName}
                    </span>
                    <span className="shrink-0">{formatMxn(l.lineTotalCents, o.currency)}</span>
                  </li>
                ))}
                {o.deliveryFeeCents !== null && (
                  <li className="flex items-baseline justify-between gap-2 text-[11px] text-ink-500">
                    <span>Envío</span>
                    <span className="shrink-0">{formatMxn(o.deliveryFeeCents, o.currency)}</span>
                  </li>
                )}
              </ul>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}
